import type { RMResourceDoc } from "resource-manager";

import { getIdToken } from "@/services/Auth";
import { getBaseApiUrl, getDefaultHeaders, request } from "@/utils/CreativeIntelligenceApi";
import { API_ROUTES } from "@/constants";

export const RESOURCE_MANAGER_STATUS = {
    AVAILABLE: "AVAILABLE",
    IN_PROGRESS: "IN_PROGRESS",
    FAILED: "FAILED"
} as const;

export const RESOURCE_MANAGER_TYPES = {
    PLAINLY_CREATE_PROJECT: "PLAINLY_CREATE_PROJECT",
    PLAINLY_RENDER: "PLAINLY_RENDER",
    PLAINLY_BATCH_RENDER: "PLAINLY_BATCH_RENDER",
    ADOBE: "ADOBE"
} as const;

type ResourceManagerInProgressResponse = {
    resourceId: string;
    status: "IN_PROGRESS";
    percentage: number;
    output?: {
        inProgress: unknown;
    };
};

export type ResourceManagerFailedResponse = {
    resourceId: string;
    status: "FAILED";
    percentage: number;
    output: {
        fail: Error | string;
    };
};

export type ResourceManagerSuccessResponse<T> = {
    resourceId: string;
    status: "AVAILABLE";
    percentage: number;
    output: {
        success: T;
    };
};

export type ResourceManagerResponse<T> =
    | ResourceManagerInProgressResponse
    | ResourceManagerFailedResponse
    | ResourceManagerSuccessResponse<T>;

export async function resourceManagerRequest<T, B = unknown>({ id, body }: { id: string; body?: B }) {
    const baseUrl = getBaseApiUrl();

    const url = `${baseUrl}${API_ROUTES.RESOURCE_MANAGER_REQUEST}/${id}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<ResourceManagerResponse<T>, B>(url, headers, "POST", body);
}

export async function resourceListRequest<T = unknown, I = unknown>({ id }: { id: string }) {
    const baseUrl = getBaseApiUrl();

    const url = `${baseUrl}${API_ROUTES.resourceManagerList(id)}`;
    const token = await getIdToken();
    const headers = getDefaultHeaders(token);

    return request<{ items: RMResourceDoc<T, I>[]; nextPageToken: string }>(url, headers, "GET");
}
